import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const FileUploader = ({
  onFileSelect,
  loading = false,
  selectedFile = null,
  onClearFile,
  error = '',
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      validateAndPassFile(file);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      validateAndPassFile(file);
    }
  };

  const validateAndPassFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (['xlsx', 'xls', 'csv'].includes(ext)) {
      onFileSelect(file);
    } else {
      alert(t('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.'));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
      />

      {!selectedFile ? (
        <Card
          variant="outlined"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: isDragOver ? 'primary.main' : 'divider',
            bgcolor: isDragOver ? 'action.hover' : 'background.paper',
            borderRadius: 3,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <CardContent sx={{ p: '0 !important' }}>
            <Stack spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.9,
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 36 }} />
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t('Drag & drop your patient list here')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('Supports Excel (.xlsx, .xls) and CSV (.csv) files')}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Chip icon={<TableChartIcon />} label="Excel (.xlsx / .xls)" size="small" variant="outlined" />
                <Chip icon={<InsertDriveFileIcon />} label="CSV (.csv)" size="small" variant="outlined" />
              </Stack>

              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
                sx={{ mt: 1, textTransform: 'none', px: 3, fontWeight: 600, borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : t('Browse File')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card
          variant="outlined"
          sx={{
            p: 2.5,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  display: 'flex',
                }}
              >
                <TableChartIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedFile.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                  <Chip
                    label={selectedFile.name.split('.').pop().toUpperCase()}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {t('Change File')}
              </Button>
              <IconButton
                color="error"
                size="small"
                onClick={onClearFile}
                title={t('Remove File')}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FileUploader;
